import { motion, type Variants } from 'framer-motion';
import {
  BrainCircuit,
  Cctv,
  Database,
  MessagesSquare,
  Radar,
  ScanEye,
  Wrench,
  type LucideIcon,
} from 'lucide-react';
import { services } from '../data/mockData';
import type { Service } from '../types';

const ICON_MAP: Record<string, LucideIcon> = {
  cctv: Cctv,
  'scan-eye': ScanEye,
  'brain-circuit': BrainCircuit,
  database: Database,
  'messages-square': MessagesSquare,
  radar: Radar,
  wrench: Wrench,
};

const container: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.1 } },
};

const item: Variants = {
  hidden: { opacity: 0, y: 28 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

function ServiceCard({ service }: { service: Service }) {
  const Icon = ICON_MAP[service.icon] ?? Cctv;

  return (
    <motion.div
      variants={item}
      className="lift-card group rounded-2xl border border-slate-100 bg-white p-8 shadow-sm"
    >
      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-cyan-50 text-cyan-600 transition-colors duration-300 group-hover:bg-gradient-to-br group-hover:from-cyan-500 group-hover:to-blue-600 group-hover:text-white">
        <Icon className="h-6 w-6" />
      </div>
      <h3 className="mt-5 text-lg font-bold text-slate-900">{service.title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-slate-600">
        {service.description}
      </p>
    </motion.div>
  );
}

export default function Services() {
  return (
    <section id="services" className="bg-slate-50 py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.4 }}
          variants={container}
          className="mx-auto max-w-2xl text-center"
        >
          <motion.p
            variants={item}
            className="text-sm font-semibold uppercase tracking-[0.3em] text-cyan-600"
          >
            Services
          </motion.p>
          <motion.h2
            variants={item}
            className="mt-3 text-3xl font-bold text-slate-900 sm:text-4xl lg:text-5xl"
          >
            安全服务
          </motion.h2>
          <motion.p variants={item} className="mt-4 text-slate-500">
            从智能监控到运维保障，一站式安全服务体系为您的业务保驾护航
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.1 }}
          variants={container}
          className="mt-16 grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3"
        >
          {services.map((service) => (
            <ServiceCard key={service.id} service={service} />
          ))}
        </motion.div>
      </div>
    </section>
  );
}
